// useSidebarBucketsState.js

import { useCallback, useEffect, useMemo, useState } from 'react';

// Context
import { useCurrentLanguage } from 'context/LanguageContext';
import { useNodeTypes } from 'context/NodeTypesContext';
import { useUser } from 'context/UserContext';
import { useWorkspace } from 'context/WorkspaceContext';

// Routing
import { useNavigate } from 'react-router-dom';

// Services
import {
    getNodeTypesCacheForScope,
    makeCompositeScopeKey,
    makeWorkspaceScopeKeyStrict,
} from 'services/nodetypes/NodeTypesCacheService';

// Utilities
import {
    buildModuleChannelRows,
    buildModuleDatabaseRows,
    buildModuleDashboardRows,
    buildWorkspaceChannels,
    buildWorkspaceDatabases,
    buildWorkspaceDashboards,
    composeBuckets,
} from '../utils/bucketTransforms';

import useRememberedOpenBucket from './useRememberedOpenBucket';
import useSidebarBucketsData from './useSidebarBucketsData';

/**
 * useSidebarBucketsState
 *
 * Manages sidebar bucket data composition, remembered open state, modal state, and navigation handlers.
 *
 * @param {object} params - Hook parameters.
 * @param {function} params.onRequestClose - Called when a mobile sidebar action should close the drawer.
 *
 * @returns {object} Grouped sidebar buckets state and handlers for the shell component.
 */
export default function useSidebarBucketsState({ onRequestClose }) {
    const language = useCurrentLanguage();
    const { getType } = useNodeTypes();
    const { user } = useUser();
    const { activeWorkspace } = useWorkspace();
    const navigate = useNavigate();

    const [dashboardModalOpen, setDashboardModalOpen] = useState(false);
    const [channelModalOpen, setChannelModalOpen] = useState(false);
    const [channelModalModuleKey, setChannelModalModuleKey] = useState(null);
    const [addDatabaseModalOpen, setAddDatabaseModalOpen] = useState(false);
    const [nodeTypesTick, setNodeTypesTick] = useState(0);

    const { modules, modulesHydrated, databases, dashboards, channels } = useSidebarBucketsData({
        workspaceKey: activeWorkspace?.key,
        viewerUserKey: user?.key,
    });

    useEffect(() => {
        const scopeKey = makeCompositeScopeKey([
            makeWorkspaceScopeKeyStrict(activeWorkspace?.key),
            'ROOT',
        ]);

        const cache = getNodeTypesCacheForScope(scopeKey);
        const unsubscribe = cache.onChange(() => {
            setNodeTypesTick((prev) => prev + 1);
        });

        return () => unsubscribe?.();
    }, [activeWorkspace?.key]);

    const activeWorkspaceKey = typeof activeWorkspace?.key === 'string'
        ? activeWorkspace.key.trim()
        : '';

    const pinnedDashboardKey = useMemo(() => {
        if (!activeWorkspaceKey) return '';

        const pinnedMap = user?.data_profile?.pinnedDashboardByWorkspace;
        if (!pinnedMap || typeof pinnedMap !== 'object') return '';

        const value = pinnedMap[activeWorkspaceKey];
        return typeof value === 'string' ? value.trim() : '';
    }, [activeWorkspaceKey, user?.data_profile?.pinnedDashboardByWorkspace]);

    const workspaceDatabases = useMemo(() => {
        return buildWorkspaceDatabases({
            databases,
            getType,
            language,
            workspaceKey: activeWorkspace?.key,
            nodeTypesTick,
        });
    }, [activeWorkspace?.key, databases, getType, language, nodeTypesTick]);

    const moduleDatabaseRows = useMemo(() => {
        return buildModuleDatabaseRows({
            modules,
            databases,
            getType,
            language,
            workspaceKey: activeWorkspace?.key,
            nodeTypesTick,
        });
    }, [activeWorkspace?.key, databases, getType, language, modules, nodeTypesTick]);

    const workspaceDashboards = useMemo(() => {
        return buildWorkspaceDashboards({
            dashboards,
            viewerUserKey: user?.key,
            pinnedDashboardKey,
        });
    }, [dashboards, pinnedDashboardKey, user?.key]);

    const moduleDashboardRows = useMemo(() => {
        return buildModuleDashboardRows({
            modules,
            dashboards,
            viewerUserKey: user?.key,
            pinnedDashboardKey,
        });
    }, [dashboards, modules, pinnedDashboardKey, user?.key]);

    const workspaceChannels = useMemo(() => {
        return buildWorkspaceChannels({ channels });
    }, [channels]);

    const moduleChannelRows = useMemo(() => {
        return buildModuleChannelRows({ modules, channels });
    }, [channels, modules]);

    const buckets = useMemo(() => {
        return composeBuckets({
            modules,
            moduleDatabaseRows,
            moduleDashboardRows,
            moduleChannelRows,
            workspaceDatabases,
            workspaceDashboards,
            workspaceChannels,
        });
    }, [moduleChannelRows, moduleDashboardRows, moduleDatabaseRows, modules, workspaceChannels, workspaceDashboards, workspaceDatabases]);

    const { openBucketKey, setOpenBucketKey } = useRememberedOpenBucket({
        userKey: user?.key,
        workspaceKey: activeWorkspace?.key,
        buckets,
        modulesHydrated,
    });

    /**
     * handleBucketToggle
     *
     * Toggles the open sidebar bucket.
     *
     * @param {string} bucketKey - Bucket key to toggle.
     */
    const handleBucketToggle = useCallback((bucketKey) => {
        setOpenBucketKey((prev) => (prev === bucketKey ? null : bucketKey));
    }, [setOpenBucketKey]);

    /**
     * handleDashboardClick
     *
     * Navigates to the selected dashboard and closes the mobile sidebar when needed.
     *
     * @param {object} dashboardRow - Dashboard row metadata.
     */
    const handleDashboardClick = useCallback((dashboardRow) => {
        const dashboardKey = dashboardRow?.key;
        if (!dashboardKey) return;

        onRequestClose?.();
        navigate(`/dashboard/${dashboardKey}`);
    }, [navigate, onRequestClose]);

    /**
     * handleChannelClick
     *
     * Navigates to the selected channel.
     *
     * @param {object} channelRow - Channel row metadata.
     */
    const handleChannelClick = useCallback((channelRow) => {
        const channelKey = channelRow?.key;
        if (!channelKey) return;

        navigate(`/channels/${channelKey}`);
    }, [navigate]);

    /**
     * handleAddFromTemplate
     *
     * Opens the create-dashboard modal when the active workspace and user are ready.
     */
    const handleAddFromTemplate = useCallback(() => {
        if (!user?.key || !activeWorkspace?.key) return;
        setDashboardModalOpen(true);
    }, [activeWorkspace?.key, user?.key]);

    /**
     * handleAddDatabase
     *
     * Opens the add-database wizard modal.
     */
    const handleAddDatabase = useCallback(() => {
        if (!user?.key || !activeWorkspace?.key) return;
        setAddDatabaseModalOpen(true);
    }, [activeWorkspace?.key, user?.key]);

    /**
     * handleAddChannel
     *
     * Opens the channel modal and scopes it to the selected module bucket when present.
     *
     * @param {object} bucket - Bucket metadata.
     */
    const handleAddChannel = useCallback((bucket) => {
        if (!user?.key || !activeWorkspace?.key) return;

        setChannelModalModuleKey(bucket?.moduleKey || null);
        setChannelModalOpen(true);
    }, [activeWorkspace?.key, user?.key]);

    return {
        bucketListState: {
            buckets,
            openBucketKey,
        },
        modalState: {
            dashboardModalOpen,
            setDashboardModalOpen,
            channelModalOpen,
            setChannelModalOpen,
            channelModalModuleKey,
            addDatabaseModalOpen,
            setAddDatabaseModalOpen,
            activeWorkspaceKey: activeWorkspace?.key || null,
        },
        handlers: {
            handleBucketToggle,
            handleDashboardClick,
            handleChannelClick,
            handleAddFromTemplate,
            handleAddChannel,
            handleAddDatabase,
        },
    };
}